import { useState } from 'react';
import { Plus, X, Pencil, Trash2, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
    useBlogTypes,
    useCreateBlogType,
    useUpdateBlogType,
    useDeleteBlogType,
} from '../hooks/useBlogAdmin';
import type { BlogType } from '../types';
import { AdminConfirmDialog } from '@/components/shared/AdminConfirmDialog';

export function BlogTypesSection() {
    const [editingId, setEditingId] = useState<string | null>(null);
    const [showCreate, setShowCreate] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [form, setForm] = useState<Partial<BlogType>>({ name: '', slug: '', color: '#15A8DC', sort_order: 0 });
    const [deactivateTypeId, setDeactivateTypeId] = useState<string | null>(null);

    const { data: types, isLoading, refetch } = useBlogTypes();
    const createMutation = useCreateBlogType();
    const updateMutation = useUpdateBlogType();
    const deleteMutation = useDeleteBlogType();

    const resetForm = () => {
        setForm({ name: '', slug: '', color: '#15A8DC', sort_order: 0 });
        setFormError(null);
        setEditingId(null);
        setShowCreate(false);
    };

    const handleCreate = async () => {
        if (!form.name?.trim() || !form.slug?.trim()) return;
        setFormError(null);
        try {
            await createMutation.mutateAsync({
                p_name: form.name.trim(),
                p_slug: form.slug.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
                p_color: form.color || '#15A8DC',
                p_sort_order: form.sort_order ?? undefined,
            });
            resetForm();
            refetch();
        } catch (e: unknown) {
            const msg = e && typeof e === 'object' && 'message' in e ? String((e as { message: string }).message) : 'Error al crear tipología';
            setFormError(msg);
        }
    };

    const handleUpdate = async () => {
        if (!editingId) return;
        setFormError(null);
        try {
            await updateMutation.mutateAsync({
                p_id: editingId,
                p_name: form.name?.trim() || undefined,
                p_slug: form.slug?.trim() ? form.slug.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : undefined,
                p_color: form.color || undefined,
                p_sort_order: form.sort_order,
            });
            resetForm();
            refetch();
        } catch (e: unknown) {
            const msg = e && typeof e === 'object' && 'message' in e ? String((e as { message: string }).message) : 'Error al actualizar tipología';
            setFormError(msg);
        }
    };

    const slugFromName = (name: string) =>
        name
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/\s+/g, '-')
            .replace(/[^a-z0-9-]/g, '');

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2">
                <button
                    onClick={() => {
                        setFormError(null);
                        setShowCreate(true);
                        setForm({ name: '', slug: '', color: '#15A8DC', sort_order: (types?.length ?? 0) });
                    }}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
                >
                    <Plus size={16} />
                    Nueva tipología
                </button>
                <button
                    onClick={() => refetch()}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground bg-muted/50 hover:bg-muted"
                >
                    <RefreshCw size={14} />
                    Actualizar
                </button>
            </div>

            {(showCreate || editingId) && (
                <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                    <h4 className="font-medium">
                        {editingId ? 'Editar tipología' : 'Nueva tipología'}
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs font-medium text-muted-foreground">Nombre</label>
                            <input
                                value={form.name ?? ''}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setForm((f) => ({
                                        ...f,
                                        name: v,
                                        slug: editingId ? f.slug : slugFromName(v),
                                    }));
                                }}
                                placeholder="ej: Consejos"
                                className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-muted-foreground">Slug</label>
                            <input
                                value={form.slug ?? ''}
                                onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                                placeholder="ej: consejos"
                                className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-muted-foreground">Color (hex)</label>
                            <input
                                type="color"
                                value={form.color ?? '#15A8DC'}
                                onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                                className="w-full mt-1 h-10 rounded-lg border border-border cursor-pointer"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-muted-foreground">Orden</label>
                            <input
                                type="number"
                                value={form.sort_order ?? 0}
                                onChange={(e) => setForm((f) => ({ ...f, sort_order: parseInt(e.target.value, 10) || 0 }))}
                                className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm"
                            />
                        </div>
                    </div>
                    {formError && (
                        <p className="text-sm text-destructive">{formError}</p>
                    )}
                    <div className="flex gap-2">
                        <button
                            onClick={editingId ? handleUpdate : handleCreate}
                            disabled={createMutation.isPending || updateMutation.isPending}
                            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
                        >
                            {editingId ? 'Guardar' : 'Crear'}
                        </button>
                        <button
                            onClick={resetForm}
                            className="px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-muted"
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            )}

            {isLoading ? (
                <div className="space-y-2">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="h-14 rounded-xl border border-border bg-card animate-pulse" />
                    ))}
                </div>
            ) : !types || types.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border bg-card/50 py-12 text-center">
                    <p className="text-sm text-muted-foreground">No hay tipologías. Crea la primera.</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {types.map((t) => (
                        <div
                            key={t.id}
                            className="flex items-center gap-4 rounded-xl border border-border bg-card px-4 py-3"
                        >
                            <div
                                className="w-3 h-3 rounded-full shrink-0"
                                style={{ backgroundColor: t.color || '#15A8DC' }}
                            />
                            <div className="flex-1 min-w-0">
                                <span className="font-medium">{t.name}</span>
                                <span className="text-muted-foreground text-sm ml-2">/{t.slug}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => {
                                        setFormError(null);
                                        setEditingId(t.id);
                                        setForm({
                                            name: t.name,
                                            slug: t.slug,
                                            color: t.color || '#15A8DC',
                                            sort_order: t.sort_order,
                                        });
                                        setShowCreate(false);
                                    }}
                                    className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted"
                                >
                                    <Pencil size={16} />
                                </button>
                                <button
                                    onClick={() => setDeactivateTypeId(t.id)}
                                    className="p-2 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <AdminConfirmDialog
                open={!!deactivateTypeId}
                variant="danger"
                title="Desactivar tipología"
                description={<p>¿Desactivar esta tipología?</p>}
                confirmLabel="Desactivar"
                onCancel={() => setDeactivateTypeId(null)}
                onConfirm={() => {
                    if (deactivateTypeId) {
                        deleteMutation.mutate(deactivateTypeId, {
                            onSuccess: () => {
                                setDeactivateTypeId(null);
                                refetch();
                            },
                        });
                    }
                }}
                confirmDisabled={deleteMutation.isPending}
            />
        </div>
    );
}
